// ===========================================
// Create a Meet & Greet from an enquiry
// ===========================================
// Dialog for booking a Meet & Greet from an enquiry, pre-filling fields from the enquiry data.
// A parent mounts it only while it is open (no reads when closed, review 5.7 M1).

import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Divider,
  Box,
  Paper,
} from '@mui/material';
import { addDays, format, startOfDay, isSameDay } from 'date-fns';
import { isTeacherAwayOnDay } from '../../utils/timeOff';
import { toLocalDateString } from '../../utils/dateUtils';
import { useInstruments } from '../../hooks/useAdmin';
import { useCreateMeetAndGreetFromEnquiry, useMeetAndGreetAdminAvailability } from '../../hooks/useMeetAndGreet';
import { Enquiry } from '../../api/enquiry.api';
import { usePermissions } from '../../hooks/usePermissions';
import { decodeMessage } from './enquiries/enquiryUi';

interface TimeSlot {
  time: string;
  booked: boolean;
}

// ===========================================
// VALIDATION SCHEMA
// ===========================================

const convertSchema = z.object({
  studentFirstName: z.string().min(1, 'Student first name is required').max(50),
  studentLastName: z.string().min(1, 'Student last name is required').max(50),
  studentAge: z.coerce.number().int().min(3, 'Age must be at least 3').max(99, 'Age must be under 100'),
  contact1FirstName: z.string().min(1, 'First name is required').max(50),
  contact1LastName: z.string().min(1, 'Last name is required').max(50),
  contact1Email: z.string().min(1, 'Email is required').email('Invalid email address'),
  contact1Phone: z.string().min(1, 'Phone is required').max(20),
  contact1Relationship: z.string().min(1, 'Relationship is required').max(50),
  instrumentId: z.string().optional().or(z.literal('')),
  instrumentText: z.string().max(100).optional().or(z.literal('')),
  additionalNotes: z.string().max(500).optional().or(z.literal('')),
});

// The age field holds what was typed (zod 4 types a coerced input as `unknown`) and is a number
// only once the schema has parsed it, so the form has two shapes.
type FormInput = z.input<typeof convertSchema>;
type FormValues = z.output<typeof convertSchema>;

// ===========================================
// PROPS
// ===========================================

interface ConvertToMeetAndGreetDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  enquiry: Enquiry | null;
}

// ===========================================
// COMPONENT
// ===========================================

export default function ConvertToMeetAndGreetDialog({
  open,
  onClose,
  onSuccess,
  enquiry,
}: ConvertToMeetAndGreetDialogProps) {
  const { can } = usePermissions();
  // The instrument list needs class_config.read; without it (or if it fails) the instrument is typed.
  const canListInstruments = can('class_config', 'read');
  const { data: instrumentList, isError: instrumentsError } = useInstruments({ enabled: open && canListInstruments });
  const instruments = canListInstruments ? instrumentList : undefined;
  const instrumentsUnavailable = !canListInstruments || instrumentsError;
  const createMutation = useCreateMeetAndGreetFromEnquiry();
  // Gated on `open` — see AdminScheduleDialog — and on view access: the availability route needs
  // meet_and_greet.read, which a Meet & Greet writer may not hold (#272).
  const mayReadMeetAndGreet = can('meet_and_greet', 'read');
  const { data: availability } = useMeetAndGreetAdminAvailability(undefined, open && mayReadMeetAndGreet);

  // Date/time picker state
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);

  // Generate available dates from M&G availability settings
  const availableDates = useMemo(() => {
    if (!availability?.timeSlots || availability.timeSlots.length === 0) return [];
    const availableDaysOfWeek = new Set(
      availability.timeSlots.filter((slot: { isActive: boolean }) => slot.isActive).map((slot: { dayOfWeek: number }) => slot.dayOfWeek)
    );
    const advanceBookingDays = availability?.settings?.advanceBookingDays || 14;
    const dates: Date[] = [];
    let current = addDays(startOfDay(new Date()), 1);
    const maxDate = addDays(new Date(), advanceBookingDays);
    while (current <= maxDate && dates.length < 10) {
      if (availableDaysOfWeek.has(current.getDay())) dates.push(startOfDay(current));
      current = addDays(current, 1);
    }
    return dates;
  }, [availability]);

  // Generate time slots for selected date
  const timeSlots = useMemo(() => {
    if (!selectedDate || !availability?.teachers || !availability?.timeSlots) return [];
    if (availability.teachers.length === 0) return [];
    const dayOfWeek = selectedDate.getDay();
    const dayTimeSlots = availability.timeSlots.filter(
      (slot: { dayOfWeek: number; isActive: boolean }) => slot.dayOfWeek === dayOfWeek && slot.isActive
    );
    if (dayTimeSlots.length === 0) return [];
    const sessionDuration = availability.settings?.sessionDuration || 15;
    const bufferTime = availability.settings?.bufferTime || 0;
    const bookedSlots = availability.bookedSlots || [];
    const teachers = availability.teachers;
    const unavailablePeriods = availability.unavailablePeriods || [];
    const selectedDayString = toLocalDateString(selectedDate);
    const slotMap = new Map<string, boolean>();
    for (const configuredSlot of dayTimeSlots) {
      const [startH, startM] = configuredSlot.startTime.split(':').map(Number);
      const [endH, endM] = configuredSlot.endTime.split(':').map(Number);
      let mins = startH * 60 + startM;
      const endMins = endH * 60 + endM;
      while (mins + sessionDuration <= endMins) {
        const h = Math.floor(mins / 60), m = mins % 60;
        const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        const anyFree = teachers.some((teacher: { id: string }) => {
          // A teacher on an APPROVED away period is not free that day at all.
          if (isTeacherAwayOnDay(unavailablePeriods, teacher.id, selectedDayString)) return false;
          const dt = new Date(selectedDate);
          dt.setHours(h, m, 0, 0);
          return !bookedSlots.some((b: { dateTime: string | null; teacherId: string | null }) =>
            b.dateTime && b.teacherId === teacher.id && isSameDay(new Date(b.dateTime), dt) &&
            new Date(b.dateTime).getHours() === h && new Date(b.dateTime).getMinutes() === m
          );
        });
        if (!slotMap.has(timeStr)) slotMap.set(timeStr, anyFree);
        else if (anyFree) slotMap.set(timeStr, true);
        mins += sessionDuration + bufferTime;
      }
    }
    return Array.from(slotMap.entries())
      .map(([time, available]) => ({ time, booked: !available }))
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [selectedDate, availability]);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(convertSchema),
    defaultValues: {
      studentFirstName: '',
      studentLastName: '',
      studentAge: '',
      contact1FirstName: '',
      contact1LastName: '',
      contact1Email: '',
      contact1Phone: '',
      contact1Relationship: 'Parent',
      instrumentId: '',
      instrumentText: '',
      additionalNotes: '',
    },
  });

  // Pre-fill ONCE per open and enquiry id: a refetch (focus) or the instrument list arriving must
  // not wipe what staff have typed. The instrument is matched later, without a reset.
  const prefilledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      prefilledFor.current = null;
      return;
    }
    if (!enquiry || prefilledFor.current === enquiry.id) return;
    prefilledFor.current = enquiry.id;
    const child = Array.isArray(enquiry.Children) ? enquiry.Children[0] : undefined;

    // Try to auto-match instrument by name (the instrument is stored as typed, not escaped).
    const matchedInstrument = instruments?.find(
      (inst) => inst.name.toLowerCase() === (enquiry.instrument ?? '').toLowerCase()
    );

    reset({
      studentFirstName: child?.firstName ?? enquiry.childFirstName ?? '',
      studentLastName: child?.lastName ?? '',
      studentAge: child?.age ?? '',
      contact1FirstName: enquiry.firstName,
      contact1LastName: enquiry.lastName,
      // A phone-only enquiry has no email: the booking needs one, so staff type it here.
      contact1Email: enquiry.email ?? '',
      contact1Phone: enquiry.phone ?? '',
      contact1Relationship: 'Parent',
      instrumentId: matchedInstrument?.id || '',
      instrumentText: matchedInstrument ? '' : (enquiry.instrument ?? ''),
      // Only the message is stored HTML-escaped (#298).
      additionalNotes: decodeMessage(enquiry.message).slice(0, 500),
    });
    setSelectedDate(null);
    setSelectedSlot(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enquiry?.id, reset]);

  // The instrument list can arrive after the pre-fill: pick the match only while the field is still
  // exactly what the pre-fill left (nothing chosen, nothing typed over it).
  useEffect(() => {
    if (!enquiry || !instruments || getValues('instrumentId')) return;
    if (getValues('instrumentText') !== (enquiry.instrument ?? '')) return;
    const matched = instruments.find((inst) => inst.name.toLowerCase() === (enquiry.instrument ?? '').toLowerCase());
    if (!matched) return;
    setValue('instrumentId', matched.id);
    setValue('instrumentText', '');
  }, [enquiry, instruments, getValues, setValue]);

  const onSubmit = async (values: FormValues) => {
    if (!enquiry) return;

    // A typed instrument (no list to pick from) travels in the notes: the booking stores an id only.
    const typedInstrument = !values.instrumentId && values.instrumentText?.trim() ? `Instrument: ${values.instrumentText.trim()}` : '';
    const notes = [typedInstrument, values.additionalNotes ?? ''].filter(Boolean).join('\n').slice(0, 500);

    try {
      await createMutation.mutateAsync({
        enquiryId: enquiry.id,
        studentFirstName: values.studentFirstName,
        studentLastName: values.studentLastName,
        studentAge: values.studentAge,
        contact1Name: `${values.contact1FirstName} ${values.contact1LastName}`.trim(),
        contact1Email: values.contact1Email,
        contact1Phone: values.contact1Phone,
        contact1Relationship: values.contact1Relationship,
        emergencyName: `${values.contact1FirstName} ${values.contact1LastName}`.trim(),
        emergencyPhone: values.contact1Phone,
        emergencyRelationship: values.contact1Relationship,
        ...(values.instrumentId ? { instrumentId: values.instrumentId } : {}),
        ...(notes ? { additionalNotes: notes } : {}),
        ...(selectedDate && selectedSlot ? {
          preferredDateTime: (() => {
            const [h, m] = selectedSlot.time.split(':').map(Number);
            const dt = new Date(selectedDate);
            dt.setHours(h, m, 0, 0);
            return dt.toISOString();
          })(),
        } : {}),
      });
    } catch {
      // The hook shows the server's reason; keep the form open so nothing typed is lost.
      return;
    }

    onSuccess();
  };

  const isPending = isSubmitting || createMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Create a Meet & Greet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Book a Meet & Greet for this enquiry. Pre-filled details can be edited below.
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Student Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Student Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="studentFirstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Student First Name"
                    required
                    fullWidth
                    size="small"
                    error={!!errors.studentFirstName}
                    helperText={errors.studentFirstName?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="studentLastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Student Last Name"
                    required
                    fullWidth
                    size="small"
                    error={!!errors.studentLastName}
                    helperText={errors.studentLastName?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="studentAge"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Student Age"
                    required
                    type="number"
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, max: 100 }}
                    error={!!errors.studentAge}
                    helperText={errors.studentAge?.message}
                  />
                )}
              />
            </Grid>

            {/* Primary Contact */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                Primary Contact
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="contact1FirstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    required
                    fullWidth
                    size="small"
                    error={!!errors.contact1FirstName}
                    helperText={errors.contact1FirstName?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="contact1LastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    required
                    fullWidth
                    size="small"
                    error={!!errors.contact1LastName}
                    helperText={errors.contact1LastName?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="contact1Email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Contact Email"
                    type="email"
                    required
                    fullWidth
                    size="small"
                    error={!!errors.contact1Email}
                    helperText={errors.contact1Email?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="contact1Phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Contact Phone"
                    fullWidth
                    size="small"
                    error={!!errors.contact1Phone}
                    helperText={errors.contact1Phone?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="contact1Relationship"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Relationship to Student"
                    required
                    fullWidth
                    size="small"
                    error={!!errors.contact1Relationship}
                    helperText={errors.contact1Relationship?.message}
                  />
                )}
              />
            </Grid>

            {/* Preferences */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                Preferences
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              {instrumentsUnavailable ? (
                <Controller
                  name="instrumentText"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Instrument"
                      fullWidth
                      size="small"
                      helperText="Added to the notes (the instrument list is not available to you)."
                      inputProps={{ maxLength: 100 }}
                    />
                  )}
                />
              ) : (
                <Controller
                  name="instrumentId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Instrument</InputLabel>
                      <Select {...field} label="Instrument">
                        <MenuItem value="">
                          <em>Not specified</em>
                        </MenuItem>
                        {instruments?.map((inst) => (
                          <MenuItem key={inst.id} value={inst.id}>
                            {inst.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}
            </Grid>

            {/* Preferred Date & Time */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Preferred Date (Optional)
              </Typography>
              {!mayReadMeetAndGreet ? (
                <Typography variant="body2" color="text.secondary">
                  Choosing a date needs access to view Meet & Greets. You can schedule later from the Meet & Greet page.
                </Typography>
              ) : availableDates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No available dates configured. You can schedule later from the Meet & Greet page.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableDates.map((date) => (
                    <Paper
                      key={date.toString()}
                      onClick={() => { setSelectedDate(date); setSelectedSlot(null); }}
                      sx={{
                        p: 1, minWidth: 65, textAlign: 'center', cursor: 'pointer',
                        border: '2px solid',
                        borderColor: selectedDate && isSameDay(selectedDate, date) ? 'primary.main' : 'divider',
                        bgcolor: selectedDate && isSameDay(selectedDate, date) ? 'primary.light' : 'background.paper',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {format(date, 'EEE')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {format(date, 'd MMM')}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </Grid>

            {selectedDate && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {format(selectedDate, 'EEEE, d MMMM')} - Available Times
                </Typography>
                {timeSlots.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No slots available</Typography>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 1 }}>
                    {timeSlots.map((slot) => (
                      <Paper
                        key={slot.time}
                        onClick={() => !slot.booked && setSelectedSlot(slot)}
                        sx={{
                          p: 1, textAlign: 'center',
                          cursor: slot.booked ? 'default' : 'pointer',
                          border: '2px solid',
                          borderColor: selectedSlot?.time === slot.time ? 'primary.main' : 'divider',
                          bgcolor: selectedSlot?.time === slot.time ? 'primary.light' : slot.booked ? '#f5f5f5' : 'background.paper',
                          opacity: slot.booked ? 0.5 : 1,
                          '&:hover': slot.booked ? {} : { borderColor: 'primary.main' },
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>{slot.time}</Typography>
                        {slot.booked && <Typography variant="caption" color="text.secondary">Booked</Typography>}
                      </Paper>
                    ))}
                  </Box>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                name="additionalNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Additional Notes"
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    error={!!errors.additionalNotes}
                    helperText={errors.additionalNotes?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} /> : undefined}
          >
            {isPending ? 'Creating...' : 'Create Meet & Greet'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
